import React, { useEffect, useRef, useState } from 'react'
import EditMenu from '../editMenu'
import { withTheme } from 'theming'
import AddSection from '../addSection'
import useTemplateHook from '../templateHook'
import { useTranslation } from 'react-i18next'
import CustomImage from '../../customImage/customImage'
import { isEmpty } from 'lodash'
import styled from '@emotion/styled'
import Marquee from 'react-fast-marquee'
import MobileNavDropDown from '../../mobileNavDropDown'
import NavBarDropDown from '../../navbarDropdown'

/*
 * AO Sports site header: fixed top bar (logo, site_map nav with dropdowns, search, add_btn1 /
 * add_btn2 or profile menu, burger below xl) plus the scrolling ticker underneath. The bar always
 * stays pinned; the ticker collapses once the page is scrolled down and comes back at the top.
 */

// Scroll distance (px) after which the ticker hides
const TICKER_HIDE_AT = 24

const StyledNavItem = styled.div`
  .nav-title {
    border-bottom: 3px solid transparent;
    transition: border-color 0.3s ease-in-out;
  }
  &:hover .nav-title,
  .nav-title.active-nav {
    border-bottom-color: ${(props) => props.highlightColor};
  }
`

const StyledDropdownItem = styled.li`
  transition: background-color 0.3s ease-in-out;
  &:hover {
    background-color: ${(props) => props.backgroundHover};
  }
`

const ArrowIcon = ({ isOpen }) => (
  <svg
    className="w-4 h-4 ml-1"
    style={{
      transition: 'transform 0.3s ease-in-out',
      transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
    }}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
  </svg>
)

const AthleteOwnedSportsHeader = ({
  componentData,
  modifyData,
  setModalState,
  isEdit,
  onDelete,
  theme,
  onAdd,
  onSwap,
}) => {
  const { t } = useTranslation()

  const { data, id } = componentData
  const { customNavigate, isLogin, userObj, getTemplateImage } = useTemplateHook()

  const [hoverState, setHoverState] = useState('')
  const [openDropIdx, setOpenDropIdx] = useState(null)
  const [openMobileMenu, setOpenMobileMenu] = useState(false)
  const [profileToggle, setProfileToggle] = useState(false)
  const [searchBarShow, setSearchBarShow] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [scrolled, setScrolled] = useState(false)
  const [activeTabIndex, setActiveTabIndex] = useState(null)

  const inputRef = useRef(null)

  const siteMap = data?.site_map || []
  const ticker = data?.scrolling_ticker
  const tickerItems = (ticker?.ticker_options || []).filter((item) => item?.text)
  const showTicker = ticker?.enabled && !isEmpty(tickerItems)
  const tickerHidden = scrolled && !isEdit

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > TICKER_HIDE_AT)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    if (searchBarShow && inputRef.current) {
      inputRef.current.focus()
    }
  }, [searchBarShow])

  useEffect(() => {
    setOpenDropIdx(null)
  }, [data])

  const handleModal = () => {
    setModalState({
      keyID: id,
      modalData: data,
    })
  }

  const handleAddModal = () => {
    onAdd({
      keyID: id,
      modalData: data,
    })
  }

  const handleHoverItems = (name) => {
    if (!isEdit) return
    setHoverState(name)
  }

  const handleRedirect = (linkObj, idx) => {
    setOpenMobileMenu(false)
    setOpenDropIdx(null)
    customNavigate('INDIRECT', linkObj, isEdit)
  }

  const submitSearch = () => {
    if (searchText) {
      customNavigate('DIRECT', `search?search_text=${searchText}`, isEdit)
      setSearchText('')
      setOpenMobileMenu(false)
      setSearchBarShow(false)
    }
  }

  const handleSearch = (event) => {
    if (event.key === 'Enter') submitSearch()
  }

  const hasLink = (linkObj) => linkObj?.type?.length > 0

  const hasSubItems = (siteMapItem) =>
    (siteMapItem?.sub_heading_data || []).some((item) => item?.text?.length > 0)

  const handleTitleClick = (siteMapItem, idx) => {
    setActiveTabIndex(idx)
    if (hasLink(siteMapItem?.link)) {
      handleRedirect(siteMapItem?.link)
    } else if (hasSubItems(siteMapItem)) {
      setOpenDropIdx((prev) => (prev === idx ? null : idx))
    }
  }

  const getStyleValue = (parentKey, childKey, defaultValue) => {
    const tempStyleConfig = data?.style_config

    if (!tempStyleConfig) {
      return defaultValue ? defaultValue : ''
    }

    const tempArr = tempStyleConfig[parentKey]?.data?.filter((info) => info.key === childKey)

    return !isEmpty(tempArr) ? (tempArr[0].value ? tempArr[0].value : defaultValue) : defaultValue
  }

  const highlightColor = getStyleValue('highlighter_color', 'primary_color', '#E4022B')
  const iconColor = getStyleValue('add_btn1', 'color', '#ffffff')

  const titlePrimaryStyle = {
    color: getStyleValue('title_primary', 'color', '#ffffff'),
    fontFamily: getStyleValue('title_primary', 'font', ''),
    textAlign: getStyleValue('title_primary', 'alignment', ''),
  }
  const titlePrimaryFormat = getStyleValue('title_primary', 'font_format', '')

  const titleSecondaryStyle = {
    color: getStyleValue('title_secondary', 'color', '#ffffff'),
    fontFamily: getStyleValue('title_secondary', 'font', ''),
    textAlign: getStyleValue('title_secondary', 'alignment', ''),
  }
  const titleSecondaryFormat = getStyleValue('title_secondary', 'font_format', '')

  const btn2Style = {
    backgroundImage: `linear-gradient(to ${getStyleValue(
      'add_btn2',
      'gradient',
      'left',
    )}, ${getStyleValue('add_btn2', 'primary_color', '#E4022B')}, ${getStyleValue(
      'add_btn2',
      'secondary_color',
      '#E4022B',
    )})`,
    color: getStyleValue('add_btn2', 'text_color', '#ffffff'),
  }

  const renderSearchIcon = (size) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={iconColor}
      strokeWidth="2"
    >
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </svg>
  )

  return (
    <>
      <div
        className={`${isEdit ? 'relative' : 'fixed'} inset-x-0 top-0 z-50 w-full ${
          isEdit && hoverState && 'component-hover-class'
        }`}
        onMouseEnter={() => handleHoverItems('TOP')}
        onMouseLeave={() => handleHoverItems('')}
      >
        {isEdit && hoverState.length > 0 && (
          <EditMenu
            onEdit={handleModal}
            onDelete={() => onDelete(id)}
            onMoveUp={() => onSwap(id, 'up')}
            onMoveDown={() => onSwap(id, 'down')}
          />
        )}
        <header
          className="flex h-[72px] max-xl:h-16 items-center gap-9 max-xl:gap-3 px-[4vw]"
          style={{
            background: getStyleValue('component_background', 'primary_color', '#0b0d10'),
            borderBottom: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          {/* logo */}
          <div className="flex-none h-10 max-xl:h-[34px]">
            <CustomImage
              imageUrl={getTemplateImage(data?.logo_s3key)}
              imgClass="h-full w-auto cursor-pointer"
              loaderClass="h-full w-20"
              loaderStyle={{ background: '#5A5A5A50' }}
              imgAlt={'Logo'}
              onClickRedirect={() => customNavigate('DIRECT', '/home', isEdit)}
            />
          </div>

          {/* desktop nav */}
          {!searchBarShow && (
            <nav className="hidden xl:flex h-full items-center gap-1">
              {siteMap.map((siteMapItem, idx) => (
                <StyledNavItem
                  key={idx}
                  className="relative h-full"
                  highlightColor={highlightColor}
                  onMouseEnter={() => hasSubItems(siteMapItem) && setOpenDropIdx(idx)}
                  onMouseLeave={() => setOpenDropIdx(null)}
                >
                  <button
                    type="button"
                    className={`nav-title flex h-full items-center px-3 text-sm bg-transparent cursor-pointer ${
                      openDropIdx === idx || activeTabIndex === idx ? 'active-nav' : ''
                    } ${titlePrimaryFormat}`}
                    style={titlePrimaryStyle}
                    onClick={() => handleTitleClick(siteMapItem, idx)}
                  >
                    {siteMapItem?.title}
                    {hasSubItems(siteMapItem) && <ArrowIcon isOpen={openDropIdx === idx} />}
                  </button>
                  {hasSubItems(siteMapItem) && openDropIdx === idx && (
                    <ul
                      className="absolute left-0 top-full min-w-[220px] rounded-lg p-2 z-[25]"
                      style={{
                        listStyle: 'none',
                        margin: 0,
                        background: getStyleValue(
                          'component_background',
                          'primary_color',
                          '#14171c',
                        ),
                        border: '1px solid rgba(255,255,255,0.12)',
                        boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
                        zIndex: 25,
                      }}
                    >
                      {siteMapItem.sub_heading_data.map(
                        (item, subIdx) =>
                          item?.text?.length > 0 && (
                            <StyledDropdownItem
                              key={subIdx}
                              backgroundHover={getStyleValue(
                                'highlighter_color',
                                'secondary_color',
                                'rgba(255,255,255,0.06)',
                              )}
                              className={`cursor-pointer whitespace-nowrap rounded-[5px] px-3 py-[9px] text-sm ${titleSecondaryFormat}`}
                              style={titleSecondaryStyle}
                              onClick={() => {
                                setActiveTabIndex(idx)
                                handleRedirect(item?.link)
                              }}
                            >
                              {item.text}
                            </StyledDropdownItem>
                          ),
                      )}
                    </ul>
                  )}
                </StyledNavItem>
              ))}
            </nav>
          )}

          {/* desktop search bar */}
          {searchBarShow ? (
            <div className="hidden xl:flex flex-1 items-center relative">
              <input
                ref={inputRef}
                type="text"
                className="w-full bg-transparent outline-none px-2 py-2 text-base placeholder:italic"
                placeholder={t('search_here')}
                style={{ color: iconColor, borderBottom: `1px solid ${iconColor}` }}
                disabled={isEdit}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyPress={handleSearch}
              />
              <button
                type="button"
                className="absolute right-0 bottom-1 px-1 text-xl bg-transparent"
                style={{ color: iconColor }}
                onClick={() => {
                  setSearchBarShow(false)
                  setSearchText('')
                }}
              >
                ×
              </button>
            </div>
          ) : (
            <div className="flex-1" />
          )}

          <div className="flex items-center gap-2">
            {!searchBarShow && (
              <button
                type="button"
                className="hidden xl:grid w-10 h-10 place-items-center rounded-full bg-transparent hover:bg-white/10"
                aria-label="Search"
                onClick={() => setSearchBarShow(true)}
              >
                {renderSearchIcon(20)}
              </button>
            )}

            {!isLogin ? (
              <>
                <button
                  type="button"
                  className="hidden xl:inline-flex h-11 items-center whitespace-nowrap rounded-md px-3 text-[15px] font-bold bg-transparent"
                  style={{ color: iconColor }}
                  onClick={() => handleRedirect(data?.add_btn1?.link)}
                >
                  {data?.add_btn1?.text}
                </button>

                <button
                  type="button"
                  className="inline-flex h-[38px] items-center whitespace-nowrap rounded-md px-4 text-sm font-bold border-0 hover:opacity-90"
                  style={btn2Style}
                  onClick={() => handleRedirect(data?.add_btn2?.link)}
                >
                  {data?.add_btn2?.text}
                </button>
              </>
            ) : (
              <div
                className="relative hidden xl:flex items-center cursor-pointer"
                onClick={() => setProfileToggle(!profileToggle)}
                onBlur={(e) => {
                  const currentTarget = e.currentTarget
                  // Give browser time to focus the next element
                  requestAnimationFrame(() => {
                    if (!currentTarget.contains(document.activeElement)) {
                      setProfileToggle(false)
                    }
                  })
                }}
                tabIndex={3}
              >
                <div
                  className="flex items-center text-[15px] font-semibold"
                  style={{ color: getStyleValue('title_primary', 'color', '#ffffff') }}
                >
                  <span>{userObj?.name ? userObj?.name.split(' ')[0] : ''}</span>
                  <ArrowIcon isOpen={profileToggle} />
                </div>
                {profileToggle && (
                  <div className="absolute right-0 top-full">
                    <NavBarDropDown
                      backgroundColor={getStyleValue(
                        'component_background',
                        'primary_color',
                        theme.c1,
                      )}
                      titleColor={getStyleValue('title_primary', 'color', '#ffffff')}
                    />
                  </div>
                )}
              </div>
            )}

            {/* burger */}
            <button
              type="button"
              className="xl:hidden grid w-10 h-10 place-items-center rounded-full bg-transparent hover:bg-white/10"
              aria-label="Menu"
              onClick={() => setOpenMobileMenu(!openMobileMenu)}
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke={iconColor}
                strokeWidth="2"
              >
                <path d="M4 7h16M4 12h16M4 17h16" />
              </svg>
            </button>
          </div>
        </header>

        {/* mobile menu */}
        {openMobileMenu && (
          <div
            className="xl:hidden w-full max-h-[calc(100vh-64px)] overflow-y-auto hidden-scrollbar-class"
            style={{
              background: getStyleValue('component_background', 'primary_color', '#0b0d10'),
              borderBottom: `1px solid ${iconColor}`,
            }}
          >
            <ul className="divide-y" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              <li className="px-6 pt-3 pb-2">
                <div className="relative w-full">
                  <input
                    style={{ border: `1px solid ${iconColor}`, color: iconColor }}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    onKeyPress={handleSearch}
                    className="w-full bg-transparent px-2 py-1 pr-8 rounded-md text-sm italic outline-none"
                    name="search"
                    placeholder={t('search_here')}
                    disabled={isEdit}
                  />
                  <button
                    type="button"
                    className="absolute right-[5px] top-1/2 -translate-y-1/2 bg-transparent"
                    aria-label="Search"
                    onClick={submitSearch}
                  >
                    {renderSearchIcon(16)}
                  </button>
                </div>
              </li>
              {siteMap.map((siteMapItem, idx) => (
                <li key={idx} className="py-2" style={{ borderTop: `1px solid ${iconColor}` }}>
                  <button
                    type="button"
                    className={`w-full flex justify-center items-center text-sm bg-transparent ${titlePrimaryFormat}`}
                    style={titlePrimaryStyle}
                    onClick={() => handleTitleClick(siteMapItem, idx)}
                  >
                    <span
                      style={{
                        textDecoration: openDropIdx === idx ? 'underline' : 'none',
                        textDecorationColor: highlightColor,
                      }}
                    >
                      {siteMapItem?.title}
                    </span>
                    {hasSubItems(siteMapItem) && <ArrowIcon isOpen={openDropIdx === idx} />}
                  </button>
                  {hasSubItems(siteMapItem) && openDropIdx === idx && (
                    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                      {siteMapItem.sub_heading_data.map(
                        (item, subIdx) =>
                          item?.text?.length > 0 && (
                            <li
                              key={subIdx}
                              className={`cursor-pointer flex justify-center py-2.5 text-sm ${titleSecondaryFormat}`}
                              style={titleSecondaryStyle}
                              onClick={() => handleRedirect(item?.link)}
                            >
                              {item.text}
                            </li>
                          ),
                      )}
                    </ul>
                  )}
                </li>
              ))}
              {!isLogin ? (
                data?.add_btn1?.enabled && (
                  <li
                    className="flex justify-center items-center py-2 cursor-pointer"
                    style={{ borderTop: `1px solid ${iconColor}` }}
                    onClick={() => handleRedirect(data?.add_btn1?.link)}
                  >
                    <span className="text-sm font-semibold" style={{ color: iconColor }}>
                      {data?.add_btn1?.text}
                    </span>
                  </li>
                )
              ) : (
                <li>
                  <MobileNavDropDown
                    titleColor={getStyleValue('title_primary', 'color', '#ffffff')}
                    borderTop={`1px solid ${iconColor}`}
                  />
                </li>
              )}
            </ul>
          </div>
        )}

        {/* ticker */}
        {showTicker && (
          <div
            className={`flex items-center gap-3.5 overflow-hidden px-[4vw] text-sm max-md:text-[13px] ${
              tickerHidden ? 'invisible h-0 opacity-0' : 'visible h-11 max-xl:h-10 opacity-100'
            }`}
            style={{
              background: getStyleValue('ticker_style', 'ticker_bg_color', '#3a0a14'),
              color: getStyleValue('ticker_style', 'ticker_text_color', '#ffffff'),
              transition: 'height 0.3s, opacity 0.3s, visibility 0.3s',
            }}
          >
            {ticker?.heading && (
              <b
                className="flex-none rounded-full px-3 py-[5px] text-xs font-bold "
                style={{
                  background: getStyleValue('ticker_style', 'heading_bg_color', '#E4022B'),
                  color: getStyleValue('ticker_style', 'heading_color', '#ffffff'),
                }}
              >
                {ticker.heading}
              </b>
            )}
            <div
              className="min-w-0 flex-1 overflow-hidden"
              style={{
                maskImage:
                  'linear-gradient(90deg, transparent, #000 32px, #000 calc(100% - 32px), transparent)',
                WebkitMaskImage:
                  'linear-gradient(90deg, transparent, #000 32px, #000 calc(100% - 32px), transparent)',
              }}
            >
              <Marquee
                pauseOnHover={true}
                direction={ticker?.scrolling_direction === 'LEFTTORIGHT' ? 'left' : 'right'}
              >
                {tickerItems.map((info, index) => (
                  <div key={index} className="flex items-center whitespace-nowrap">
                    <span
                      className={`${hasLink(info?.link) ? 'cursor-pointer hover:underline' : ''}`}
                      onClick={() => hasLink(info?.link) && handleRedirect(info?.link)}
                    >
                      {info.text}
                    </span>
                    <span
                      className="mx-6 inline-block w-1.5 h-1.5 rounded-full"
                      style={{
                        background: getStyleValue('ticker_style', 'ticker_dot_color', '#E4022B'),
                      }}
                    />
                  </div>
                ))}
              </Marquee>
            </div>
          </div>
        )}
        {isEdit && hoverState.length > 0 && <AddSection openAdd={handleAddModal} />}
      </div>
      {/* Reserves header + ticker height so page content starts below them */}
      {!isEdit && (
        <div
          aria-hidden="true"
          className={showTicker ? 'h-[116px] max-xl:h-[104px]' : 'h-[72px] max-xl:h-16'}
        />
      )}
    </>
  )
}

export default withTheme(AthleteOwnedSportsHeader)
